import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { PluginRoleGuard } from 'app/core/auth/guards/plugin-role.guard';
import { LayoutComponent } from 'app/layout/layout.component';

export const appRoutes: Route[] = [
    { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
    { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'dashboard' },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'forgot-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/forgot-password/forgot-password.routes'
                    ),
            },
            {
                path: 'reset-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/reset-password/reset-password.routes'
                    ),
            },
            {
                path: 'sign-in',
                loadChildren: () =>
                    import('app/modules/auth/sign-in/sign-in.routes'),
            },
            /**{
                path: 'verify-otp',
                loadChildren: () => import('app/modules/auth/otp/otp.routes'),
            },**/
            {
                path: 'unauthorised',
                loadChildren: () =>
                    import(
                        'app/modules/auth/unauthorised/unauthorised.routing'
                    ),
            },
            {
                path: 'unauthorized-access',
                loadChildren: () =>
                    import(
                        'app/modules/error/unauthorized/unauthorized-access.routes'
                    ),
            },
            {
                path: '404',
                loadChildren: () =>
                    import('app/modules/error/404/error-404.routes'),
            },
        ],
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'sign-out',
                loadChildren: () =>
                    import('app/modules/auth/sign-out/sign-out.routes'),
            },
            {
                path: 'unlock-session',
                loadChildren: () =>
                    import(
                        'app/modules/auth/unlock-session/unlock-session.routes'
                    ),
            },
        ],
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'dashboard',
                loadChildren: () =>
                    import('app/modules/dashboard/dasboard.routes'),
            },
            {
                path: 'users',
                canActivate: [PluginRoleGuard],
                data: {
                    roles: ['owner', 'manager'],
                },
                loadChildren: () =>
                    import('app/modules/config/users/users.routes'),
            },
            {
                path: 'issued-invoices',
                canActivate: [PluginRoleGuard],
                data: {
                    roles: ['manager', 'owner'],
                },
                loadChildren: () =>
                    import('app/modules/invoicing/invoicing.routes'),
            },
            {
                path: 'failed-invoices',
                canActivate: [PluginRoleGuard],
                data: {
                    roles: ['manager', 'owner'],
                },
                loadChildren: () =>
                    import('app/modules/invoicing/invoicing.failed.routes'),
            },
            {
                path: 'items-list',
                canActivate: [PluginRoleGuard],
                data: {
                    roles: ['manager', 'owner', 'stockadmin'],
                },
                loadChildren: () =>
                    import(
                        'app/modules/stock-management/inventory/inventory.routes'
                    ),
            },
            {
                path: 'advanced-inventory',
                canActivate: [PluginRoleGuard],
                data: {
                    plugins: ['adv_inventory_mgmt'],
                    roles: ['owner', 'stockadmin', 'manager'],
                },
                loadChildren: () =>
                    import(
                        'app/modules/adv-stock-management/adv-inventory/adv-inventory.routes'
                    ),
            },
            {
                path: 'advanced-inventory-purchases',
                canActivate: [PluginRoleGuard],
                data: {
                    plugins: ['adv_inventory_mgmt'],
                    roles: ['owner', 'stockadmin', 'manager'],
                },
                loadChildren: () =>
                    import(
                        'app/modules/adv-stock-management/adv-inventory-purchases/adv-inventory-purchases.routes'
                    ),
            },
            {
                path: 'advanced-inventory-adjustments',
                canActivate: [PluginRoleGuard],
                data: {
                    plugins: ['adv_inventory_mgmt'],
                    roles: ['owner', 'stockadmin', 'manager'],
                },
                loadChildren: () =>
                    import(
                        'app/modules/adv-stock-management/adv-inventory-adjustments/adv-inventory-adjustments.routes'
                    ),
            },
            {
                path: 'advanced-inventory-transfers',
                canActivate: [PluginRoleGuard],
                data: {
                    plugins: ['adv_inventory_mgmt'],
                    roles: ['owner', 'stockadmin', 'manager'],
                },
                loadChildren: () =>
                    import(
                        'app/modules/adv-stock-management/adv-inventory-transfers/adv-inventory-transfers.routes'
                    ),
            },
            {
                path: 'advanced-inventory-adjustment',
                canActivate: [PluginRoleGuard],
                data: {
                    plugins: ['adv_inventory_mgmt'],
                    roles: ['owner', 'stockadmin', 'manager'],
                },
                loadComponent: () =>
                    import(
                        'app/modules/adv-stock-management/adv-inventory-adjustments/create-adv-adjustment/create-adv-adjustment.component'
                    ).then((m) => m.CreateAdvAdjustmentComponent),
            },
            {
                path: 'advanced-inventory-purchase',
                canActivate: [PluginRoleGuard],
                data: {
                    plugins: ['adv_inventory_mgmt'],
                    roles: ['owner', 'stockadmin', 'manager'],
                },
                loadComponent: () =>
                    import(
                        'app/modules/adv-stock-management/adv-inventory-purchases/create-adv-purchase/create-adv-purchase.component'
                    ).then((m) => m.CreateAdvPurchaseComponent),
            },
            {
                path: 'advanced-inventory-transfer',
                canActivate: [PluginRoleGuard],
                data: {
                    plugins: ['adv_inventory_mgmt'],
                    roles: ['owner', 'stockadmin', 'manager'],
                },
                loadComponent: () =>
                    import(
                        'app/modules/adv-stock-management/adv-inventory-transfers/create-transfer/create-transfer.component'
                    ).then((m) => m.CreateTransferComponent),
            },
            {
                path: 'customers',
                loadChildren: () =>
                    import('app/modules/invoicing/customers/customers.routes'),
            },
            {
                path: 'suppliers',
                canActivate: [PluginRoleGuard],
                data: {
                    roles: ['manager', 'owner', 'stockadmin'],
                },
                loadChildren: () =>
                    import(
                        'app/modules/stock-management/suppliers/supplier.routes'
                    ),
            },
            {
                path: 'purchases',
                canActivate: [PluginRoleGuard],
                data: {
                    roles: ['manager', 'owner', 'stockadmin'],
                },
                loadChildren: () =>
                    import(
                        'app/modules/stock-management/purchases/purchases.routes'
                    ),
            },
            /**{
                path: 'create-purchase',
                loadChildren: () =>
                    import(
                        'app/modules/stock-management/purchases/purchase/create-purchase.routes'
                    ),
            },**/
            {
                path: 'issue-invoice',
                loadChildren: () =>
                    import('app/modules/invoicing/invoice/add-invoice.routes'),
            },
            /**{
                path: 'create-stock-transfer',
                loadChildren: () =>
                    import(
                        'app/modules/stock-management/stock-transfer/transfer/transfer.routes'
                    ),
            },**/
            {
                path: 'stock-transfers',
                canActivate: [PluginRoleGuard],
                data: {
                    roles: ['manager', 'owner', 'stockadmin'],
                },
                loadChildren: () =>
                    import(
                        'app/modules/stock-management/stock-transfer/transfer-list/transfer-list.routes'
                    ),
            },
            {
                path: 'adjustments',
                canActivate: [PluginRoleGuard],
                data: {
                    roles: ['manager', 'owner', 'stockadmin'],
                },
                loadChildren: () =>
                    import(
                        'app/modules/stock-management/adjustments/adjustments.routes'
                    ),
            },
            {
                path: 'manufacture',
                canActivate: [PluginRoleGuard],
                data: {
                    roles: ['manager', 'owner', 'stockadmin'],
                },
                loadChildren: () =>
                    import(
                        'app/modules/stock-management/manufacture/manufacture.routes'
                    ),
            },
            /**{
                path: 'create-adjustment',
                loadChildren: () =>
                    import(
                        'app/modules/stock-management/adjustments/adjustment/create-adjustment.routes'
                    ),
            },**/
            {
                path: 'buddy-sales-report',
                loadChildren: () =>
                    import(
                        'app/modules/reports/buddy-reports/buddy-invoice-reports/buddy-invoice-report.routes'
                    ),
            },
            {
                path: 'buddy-items-sales-report',
                loadChildren: () =>
                    import(
                        'app/modules/reports/buddy-reports/buddy-item-wise-report/buddy-item-wise-report.routes'
                    ),
            },
            {
                path: 'buddy-customer-sales-report',
                loadChildren: () =>
                    import(
                        'app/modules/reports/buddy-reports/buddy-customer-sales-report/buddy-customer-sales-report.routes'
                    ),
            },
            {
                path: 'efris-sales-report',
                canActivate: [PluginRoleGuard],
                data: {
                    roles: ['owner', 'manager'],
                },
                loadChildren: () =>
                    import(
                        'app/modules/reports/efris-reports/efris-invoices-report/efris-invoices-report.routes'
                    ),
            },
            {
                path: 'sales-routes',
                canActivate: [PluginRoleGuard],
                data: {
                    plugins: ['dstrbtr_sales_sht'],
                    roles: ['owner', 'manager'],
                },
                loadChildren: () =>
                    import(
                        'app/modules/sales-distribution/sales-routes/sales-routes.routes'
                    ),
            },
            {
                path: 'sales-assignments',
                canActivate: [PluginRoleGuard],
                data: {
                    plugins: ['dstrbtr_sales_sht'],
                    roles: ['owner', 'manager'],
                },
                loadChildren: () =>
                    import(
                        'app/modules/sales-distribution/sales-assignments/sales-assignments.routes'
                    ),
            },
            {
                path: 'discounts',
                canActivate: [PluginRoleGuard],
                data: {
                    plugins: ['prce_lvls'],
                    roles: ['owner', 'manager'],
                },
                loadChildren: () =>
                    import(
                        'app/modules/invoicing/price-levels/price-levels.routes'
                    ),
            },
            {
                path: 'discount-reports',
                canActivate: [PluginRoleGuard],
                data: {
                    plugins: ['prce_lvls'],
                    roles: ['owner', 'manager'],
                },
                loadChildren: () =>
                    import(
                        'app/modules/reports/buddy-reports/price-level-reports/price-level-reports.routes'
                    ),
            },
            {
                path: 'stockists',
                canActivate: [PluginRoleGuard],
                data: {
                    plugins: ['adv_sales_stkst'],
                    roles: ['owner', 'manager'],
                },
                loadChildren: () =>
                    import('app/modules/invoicing/stockists/stockists.routes'),
            },
            {
                path: 'credit-notes',
                canActivate: [PluginRoleGuard],
                data: {
                    roles: ['manager', 'owner'],
                },
                loadChildren: () =>
                    import('app/modules/credit-notes/credit-notes.routes'),
            },
            {
                path: 'system-settings',
                canActivate: [PluginRoleGuard],
                data: {
                    roles: ['manager', 'owner'],
                },
                loadChildren: () =>
                    import(
                        'app/modules/system-settings/system-settings.routes'
                    ),
            },
            {
                path: 'clients',
                canActivate: [PluginRoleGuard],
                data: {
                    roles: ['admin'],
                },
                loadComponent: () =>
                    import(
                        'app/modules/clients/clients-list/clients-list.component'
                    ).then((m) => m.ClientsListComponent),
            },
            {
                path: 'pricing',
                canActivate: [PluginRoleGuard],
                data: {
                    roles: ['admin'],
                },
                loadComponent: () =>
                    import(
                        'app/modules/pricing/pricing-list/pricing-list.component'
                    ).then((m) => m.PricingListComponent),
            },
        ],
    },

    // Catch-all route for 404 errors - must be last
    {
        path: '**',
        redirectTo: '404',
    },
];
